import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatDate, formatCOP } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Disclaimer } from "../components/Disclaimer";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import { KitAnchoringPanel } from "../components/n2/KitAnchoringPanel";
import { KitMarketSummary } from "../components/n2/KitMarketSummary";

/**
 * Fase 11.3.B — Revisión humana del Kit Documental Nivel 2.
 *
 * Reviewer asignado al case puede:
 * - Ver el kit completo (read-only de las 5 secciones).
 * - Marcar cada item del checklist consolidado (10 items).
 * - Aprobar el paquete (genera ZIP descargable).
 * - Solicitar cambios al equipo de generación (con comentarios obligatorios).
 *
 * Gates (mirror del backend):
 * - reviewer asignado al case (else 403)
 * - case.state = nivel2_needs_human_review (else 422)
 * - approve requiere checklist completo (10/10 con reviewer_decision)
 * - request-changes requiere comentarios ≥30 chars
 */

const STATUS_STYLES = {
  pass: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", label: "PASS" },
  fail: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-900", label: "FAIL" },
  manual: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", label: "MANUAL" },
};

export default function ReviewerKitDocumentalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [complete, setComplete] = useState(false);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesComments, setChangesComments] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [anchoring, setAnchoring] = useState(null);

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: kitData }, { data: checkData }, anch] = await Promise.all([
        api.get(`/reviewer/cases/${id}/kit-documental`),
        api.get(`/reviewer/cases/${id}/kit-documental/checklist`),
        api.get(`/reviewer/cases/${id}/kit-documental/anchoring`).then((r) => r.data).catch(() => null),
      ]);
      setAnchoring(anch);
      setKit(kitData.kit || kitData);
      setChecklist(checkData.items || []);
      setComplete(Boolean(checkData.complete));
      setProblems(checkData.problems || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el Kit");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateItem = (itemId, patch) => {
    setChecklist((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
  };

  const saveChecklist = async () => {
    setSaving(true);
    try {
      const payload = {
        items: checklist.map((it) => ({
          id: it.id,
          reviewer_decision: it.reviewer_decision || null,
          reviewer_reason: it.reviewer_reason || null,
        })),
      };
      const { data } = await api.post(`/reviewer/cases/${id}/kit-documental/checklist`, payload);
      setChecklist(data.items || []);
      setComplete(Boolean(data.complete));
      setProblems(data.problems || []);
      toast.success(data.complete ? "Checklist completo y guardado" : "Checklist guardado. Aún faltan ítems.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar el checklist");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!complete) {
      toast.error("Completa los 10 ítems del checklist antes de aprobar.");
      return;
    }
    if (anchoring && !anchoring.complete) {
      toast.error(`Confirma las secciones sin respaldo: ${anchoring.pending_confirmation.join(", ")}.`);
      return;
    }
    const ok = await confirm({
      title: "Aprobar Kit Documental",
      description: "¿Confirmas que has revisado el Kit Documental completo y autorizas su entrega? Se generará el paquete ZIP descargable y el caso pasará a 'Kit Documental entregado'.",
      confirmLabel: "Sí, aprobar y generar ZIP",
      testIdPrefix: "approve-n2-confirm",
    });
    if (!ok) return;
    setApproving(true);
    try {
      const { data } = await api.post(`/reviewer/cases/${id}/kit-documental/approve`, {
        comments: "Aprobado por revisor.",
      });
      toast.success("Kit Documental aprobado. El usuario podrá descargarlo.");
      if (data.fallbacks_used && data.fallbacks_used.length > 0) {
        toast.info(`Algunas secciones usaron formato HTML/CSV como respaldo: ${data.fallbacks_used.join(", ")}`);
      }
      setTimeout(() => navigate(`/reviewer/cases/${id}`), 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.pending_confirmation) {
        toast.error(detail.message);
        loadAll();
      } else if (typeof detail === "object" && detail?.problems) {
        toast.error(`Checklist incompleto: ${detail.problems.length} problemas.`);
      } else {
        toast.error(formatApiError(detail) || "No se pudo aprobar");
      }
    } finally {
      setApproving(false);
    }
  };

  const handleRequestChanges = async () => {
    if (changesComments.trim().length < 30) {
      toast.error("Los comentarios deben tener mínimo 30 caracteres.");
      return;
    }
    setRequesting(true);
    try {
      await api.post(`/reviewer/cases/${id}/kit-documental/request-changes`, {
        comments: changesComments,
      });
      toast.success("Solicitud de cambios enviada al equipo.");
      setShowChangesModal(false);
      setTimeout(() => navigate(`/reviewer/cases/${id}`), 1200);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-prose py-12">
        <p className="text-sm text-gray-500">Cargando Kit Documental…</p>
      </div>
    );
  }

  if (!kit) {
    return (
      <div className="container-prose py-12">
        <p className="text-rose-700">No se pudo cargar el Kit Documental.</p>
        <Link to={`/reviewer/cases/${id}`} className="text-primary text-sm">← Volver al caso</Link>
      </div>
    );
  }

  const validated = kit.validated_output || {};
  const studies = validated.studies || {};
  const sector = validated.sector || {};
  const risks = validated.risks || {};
  const invitation = validated.invitation || {};
  const checklistSection = validated.checklist || {};

  return (
    <div className="container-prose py-10" data-testid="reviewer-n2-kit-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-2">
          <Link to={`/reviewer/cases/${id}`} className="text-xs text-gray-500 hover:text-primary">
            ← Volver al caso
          </Link>
        </div>

        <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-1">
          Kit Documental Nivel 2 · Revisión humana obligatoria
        </p>
        <h1 className="font-serif text-3xl text-gray-900 mb-2">Revisión del Kit Documental</h1>
        <p className="text-sm text-gray-600 mb-6">
          Versión {kit.version || 1} · Modelo {kit.model_used || "claude-sonnet-4.5"} ·
          Generado {formatDate(kit.created_at)}
        </p>

        {/* Alerta de status */}
        <div className={`mb-6 p-4 border ${complete ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
          <p className="text-sm font-medium" data-testid="checklist-status">
            {complete
              ? "✓ Checklist completo. Puedes aprobar el Kit Documental."
              : `⚠ Checklist incompleto: ${problems.length} ítem(s) requieren tu decisión.`}
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mb-6">
          <Disclaimer variant="banner" />
        </div>

        {/* Kit content preview */}
        <section className="mb-6 border border-gray-200 bg-white p-6" data-testid="kit-preview">
          <h2 className="font-serif text-xl text-gray-900 mb-3">Resumen del Kit (5 secciones)</h2>

          <Block title="1. Estudios previos">
            <p><strong>Necesidad:</strong> {studies.necesidad?.descripcion || "pendiente de definir"}</p>
            <p><strong>Objeto:</strong> {studies.objeto_contractual?.detalle || "pendiente de definir"}</p>
            <p><strong>Presupuesto:</strong> {studies.presupuesto?.valor_estimado_cop && !isNaN(Number(studies.presupuesto.valor_estimado_cop)) ? formatCOP(Number(studies.presupuesto.valor_estimado_cop)) : studies.presupuesto?.valor_estimado_cop || "pendiente"}</p>
            {studies.presupuesto?.rango_referencia_mercado?.texto && (
              <p className="text-xs text-emerald-800" data-testid="kit-range-reference">{studies.presupuesto.rango_referencia_mercado.texto}</p>
            )}
            <p><strong>Modalidad:</strong> {studies.modalidad?.propuesta || "pendiente"}</p>
            <p className="text-xs text-gray-500 mt-1">
              Sources: {(studies.sources || []).length} · Limitations: {(studies.limitations || []).length}
            </p>
          </Block>

          <Block title="2. Análisis del sector">
            <p>{sector.resumen || "pendiente"}</p>
            <p className="text-xs text-gray-500 mt-1">
              Oferentes: {(sector.oferentes_potenciales || []).length} · Limitations: {(sector.limitations || []).length}
            </p>
          </Block>

          <Block title="3. Matriz de riesgos">
            <p>{risks.resumen_matriz || "pendiente"}</p>
            <p className="text-xs text-gray-500 mt-1">
              Total riesgos: {(risks.riesgos || []).length} ·
              Legales: {(risks.riesgos || []).filter(r => r.categoria === "legal").length}
            </p>
          </Block>

          <Block title="4. Invitación / pliego">
            <p className="text-xs text-gray-500">
              Etapas cronograma: {(invitation.cronograma || []).length} ·
              Criterios: {(invitation.criterios_evaluacion || []).length} ·
              Garantías: {(invitation.garantias_exigidas || []).length}
            </p>
          </Block>

          <Block title="5. Checklist legal">
            <p className="text-xs text-gray-500">
              Items: {(checklistSection.items || []).length} ·
              Categorías: {new Set((checklistSection.items || []).map(i => i.categoria)).size}
            </p>
          </Block>
        </section>

        <KitMarketSummary caseId={id} />

        <KitAnchoringPanel caseId={id} anchoring={anchoring} onChange={setAnchoring} disabled={approving} />

        {/* Checklist editor */}
        <section className="mb-6 border border-gray-200 bg-white p-6" data-testid="reviewer-checklist">
          <h2 className="font-serif text-xl text-gray-900 mb-1">Checklist consolidado (10 ítems)</h2>
          <p className="text-xs text-gray-500 mb-4">
            Marca cada ítem como <strong>Pasa</strong> o <strong>No pasa</strong>. Los ítems con
            auto-status &quot;fail&quot; o &quot;manual&quot; requieren justificación escrita.
          </p>

          <ul className="space-y-3">
            {checklist.map((it) => {
              const s = STATUS_STYLES[it.auto_status] || STATUS_STYLES.manual;
              const needsReason =
                (it.auto_status === "fail" && it.reviewer_decision === "pass") ||
                it.reviewer_decision === "fail";
              return (
                <li key={it.id} className="border border-gray-200 p-3" data-testid={`checklist-item-${it.id}`}>
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{it.label}</p>
                      <p className="text-xs text-gray-600">{it.description}</p>
                    </div>
                    <span className={`text-[10px] font-mono uppercase px-2 py-1 border ${s.bg} ${s.border} ${s.text} flex-shrink-0`}>
                      {s.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 italic mb-2">{it.auto_reason}</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <Button
                      size="sm"
                      variant={it.reviewer_decision === "pass" ? "default" : "outline"}
                      onClick={() => updateItem(it.id, { reviewer_decision: "pass" })}
                      className="rounded-sm h-8 text-xs"
                      data-testid={`item-${it.id}-pass`}
                    >
                      Pasa
                    </Button>
                    <Button
                      size="sm"
                      variant={it.reviewer_decision === "fail" ? "destructive" : "outline"}
                      onClick={() => updateItem(it.id, { reviewer_decision: "fail" })}
                      className="rounded-sm h-8 text-xs"
                      data-testid={`item-${it.id}-fail`}
                    >
                      No pasa
                    </Button>
                  </div>
                  {needsReason && (
                    <div className="mt-2">
                      <Textarea
                        value={it.reviewer_reason || ""}
                        onChange={(e) => updateItem(it.id, { reviewer_reason: e.target.value })}
                        placeholder={
                          it.reviewer_decision === "fail"
                            ? "Justifica por qué este ítem no pasa (mín. 30 caracteres)"
                            : "Justifica tu override (mín. 30 caracteres)"
                        }
                        rows={2}
                        className="text-xs"
                        data-testid={`item-${it.id}-reason`}
                      />
                      <p className="text-[10px] text-gray-500 mt-1">
                        {(it.reviewer_reason || "").length} / mín. 30 caracteres
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex gap-3">
            <Button onClick={saveChecklist} disabled={saving} variant="outline" className="rounded-sm" data-testid="save-checklist">
              {saving ? "Guardando…" : "Guardar checklist"}
            </Button>
          </div>
        </section>

        {/* Acciones */}
        <section className="mb-6 border border-gray-200 bg-white p-6">
          <h2 className="font-serif text-xl text-gray-900 mb-4">Decisión final</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleApprove}
              disabled={!complete || approving || (anchoring && !anchoring.complete)}
              className="rounded-sm h-11 px-6"
              data-testid="approve-n2-btn"
            >
              {approving ? "Aprobando…" : "Aprobar Kit Documental"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowChangesModal(true)}
              disabled={approving}
              className="rounded-sm h-11 px-6 border-rose-300 text-rose-800 hover:bg-rose-50"
              data-testid="request-changes-btn"
            >
              Solicitar cambios
            </Button>
          </div>
          {!complete && (
            <p className="text-xs text-amber-700 mt-3">
              Para aprobar, completa los 10 ítems del checklist con tu decisión.
            </p>
          )}
          {anchoring && !anchoring.complete && (
            <p className="text-xs text-amber-700 mt-1" data-testid="approve-blocked-anchoring">
              Para aprobar, confirma las secciones sin respaldo del formulario ni de SECOP.
            </p>
          )}
        </section>
      </div>

      {/* Modal request changes */}
      {showChangesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setShowChangesModal(false)}>
          <div className="bg-white max-w-lg w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="changes-modal">
            <h3 className="font-serif text-xl text-gray-900 mb-3">Solicitar cambios al Kit Documental</h3>
            <p className="text-sm text-gray-600 mb-3">
              Explica con detalle qué ajustes requieres del equipo de generación. Mínimo 30 caracteres.
            </p>
            <Textarea
              value={changesComments}
              onChange={(e) => setChangesComments(e.target.value)}
              rows={5}
              placeholder="Ej: La matriz de riesgos no contempla riesgo regulatorio sobre Decreto 1860. Por favor agregar..."
              className="mb-1"
              data-testid="changes-comments"
            />
            <p className="text-[11px] text-gray-500 mb-4">
              {changesComments.length} / mín. 30 caracteres
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowChangesModal(false)} disabled={requesting} className="rounded-sm">
                Cancelar
              </Button>
              <Button
                onClick={handleRequestChanges}
                disabled={requesting || changesComments.trim().length < 30}
                className="rounded-sm"
                data-testid="submit-changes"
              >
                {requesting ? "Enviando…" : "Enviar solicitud"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mb-3 pb-3 border-b border-gray-100 last:border-b-0">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{title}</p>
      <div className="text-sm text-gray-800 space-y-1">{children}</div>
    </div>
  );
}
